import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { X, User, Mail, Shield, Eye, EyeOff } from 'lucide-react';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const AddAdminModal = ({ isOpen, onClose, admin = null, isEdit = false }) => {
  const queryClient = useQueryClient();
  const { user } = useAuth(); // Get current user for role-based permissions
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: 'ADMIN',
    designation: '',
    regionalOffice: '',
    permissions: {
      dashboard: { view: true, write: false, delete: false },
      drivers: { view: true, write: false, delete: false },
      vehicles: { view: true, write: false, delete: false },
      passengers: { view: true, write: false, delete: false },
      trips: { view: true, write: false, delete: false },
      hotspot: { view: true, write: false, delete: false },
      peakhours: { view: true, write: false, delete: false},
      regional_offices: { view: true, write: false, delete: false },
      vendors: { view: true, write: false, delete: false },
      tickets: { view: true, write: false, delete: false },
      roles: { view: true, write: false, delete: false },
      fare: { view: true, write: false, delete: false },
      actingDriverFare: { view: true, write: false, delete: false },
      driverSimulator: { view: true, write: false, delete: false },
      SubscriptionPlan: { view: true, write: false, delete: false },
      financial: { view: true, write: false, delete: false },
      appconfig: { view: true, write: false, delete: false },
      feedback: { view: true, write: false, delete: false },
      settings: { view: true, write: false, delete: false },
      rideMatchSettings: { view: true, write: false, delete: false }
    },
    isActive: true
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Get available roles based on current user's permissions
  const getAvailableRoles = () => {
    if (!user) return [];
    
    switch (user.role) {
      case 'SUPER_ADMIN':
        return [
          { value: 'ADMIN', label: 'Admin' },
          { value: 'MODERATOR', label: 'Moderator' },
          { value: 'regional_admin', label: 'Regional Admin' },
          { value: 'regional_employee', label: 'Regional Employee' },
          { value: 'telecaller', label: 'Telecaller' }
        ];
      case 'regional_admin':
        return [
          { value: 'regional_employee', label: 'Regional Employee' },
          { value: 'telecaller', label: 'Telecaller' }
        ];
      case 'ADMIN':
        return [
          { value: 'ADMIN', label: 'Admin' },
          { value: 'MODERATOR', label: 'Moderator' },
          { value: 'telecaller', label: 'Telecaller' }
        ];
      default:
        return [];
    }
  };

  // Get available permissions based on current user's permissions
  const getAvailablePermissions = () => {
    console.log('🔍 Current user:', user);
    console.log('🔍 User role:', user?.role);
    console.log('🔍 User permissions:', user?.permissions);
    
    if (!user) return {};
    
    // For super admin, show all permissions
    if (user.role === 'SUPER_ADMIN') {
      const allPermissions = {
        dashboard: { view: true, write: false, delete: false },
        drivers: { view: true, write: false, delete: false },
        vehicles: { view: true, write: false, delete: false },
        passengers: { view: true, write: false, delete: false },
        trips: { view: true, write: false, delete: false },
        hotspot: { view: true, write: false, delete: false },
        peakhours: { view: true, write: false, delete: false},
        regional_offices: { view: true, write: false, delete: false },
        vendors: { view: true, write: false, delete: false },
        tickets: { view: true, write: false, delete: false },
        roles: { view: true, write: false, delete: false },
        fare: { view: true, write: false, delete: false },
        actingDriverFare: { view: true, write: false, delete: false },
        driverSimulator: { view: true, write: false, delete: false },
        SubscriptionPlan: { view: true, write: false, delete: false },
        financial: { view: true, write: false, delete: false },
        appconfig: { view: true, write: false, delete: false },
        feedback: { view: true, write: false, delete: false },
        settings: { view: true, write: false, delete: false },
        rideMatchSettings: { view: true, write: false, delete: false }
      };
      console.log('🔍 All permissions for super admin:', allPermissions);
      return allPermissions;
    }
    
    // For other users, only show permissions they have access to
    if (!user.permissions) return {};
    
    const availablePermissions = {};
    Object.keys(user.permissions).forEach(module => {
      if (user.permissions[module] && (user.permissions[module].view || user.permissions[module].write)) {
        availablePermissions[module] = user.permissions[module];
      }
    });
    
    console.log('🔍 Available permissions for other users:', availablePermissions);
    return availablePermissions;
  };

  // Get default role for current user
  const getDefaultRole = () => {
    const availableRoles = getAvailableRoles();
    return availableRoles.length > 0 ? availableRoles[0].value : 'ADMIN';
  };

  // Check if regional office input should be shown
  const shouldShowRegionalOffice = () => {
    return user?.role === 'SUPER_ADMIN' && formData.role === 'regional_employee';
  };

  // Update form data when admin prop changes (for edit mode)
  useEffect(() => {
    console.log('🔄 AddAdminModal useEffect triggered:', { admin, isEdit });
    
    const availablePermissions = getAvailablePermissions();
    
    if (admin && isEdit) {
      console.log('📝 Setting form data for edit mode:', admin);
      setFormData({
        name: admin.name || '',
        email: admin.email || '',
        phone: admin.phone || '',
        password: '',
        confirmPassword: '',
        role: admin.role || getDefaultRole(),
        designation: admin.designation || '',
        regionalOffice: admin.regionalOffice || '',
        permissions: admin.permissions || availablePermissions,
        isActive: admin.isActive !== false
      });
    } else {
      console.log('🆕 Resetting form for create mode');
      // Reset form for create mode with available permissions
      setFormData({
        name: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        role: getDefaultRole(),
        designation: '',
        regionalOffice: '',
        permissions: availablePermissions,
        isActive: true
      });
    }
  }, [admin, isEdit, user]);

  const createMutation = useMutation(
    (data) => api.post('/roles', data),
    {
      onSuccess: () => {
        toast.success('Admin created successfully');
        queryClient.invalidateQueries(['roles']);
        onClose();
        resetForm();
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to create admin');
      }
    }
  );

  const updateMutation = useMutation(
    (data) => api.put(`/roles/${admin._id}`, data),
    {
      onSuccess: () => {
        toast.success('Admin updated successfully');
        queryClient.invalidateQueries(['roles']);
        onClose();
        resetForm();
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to update admin');
      }
    }
  );

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      role: getDefaultRole(),
      designation: '',
      regionalOffice: '',
      permissions: getAvailablePermissions(),
      isActive: true
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePermissionChange = (module, permission, value) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [module]: {
          ...prev.permissions[module],
          [permission]: value
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    if (!formData.email.trim()) {
      toast.error('Email is required');
      return;
    }

    if (!isEdit && !formData.password) {
      toast.error('Password is required');
      return;
    }

    if (!isEdit && formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!isEdit && formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    // Prepare data for submission
    const submitData = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      role: formData.role,
      designation: formData.designation.trim(),
      permissions: formData.permissions,
      isActive: formData.isActive
    };

    // Only include regional office if user is super admin
    if (user?.role === 'SUPER_ADMIN' && formData.regionalOffice) {
      submitData.regionalOffice = formData.regionalOffice.trim();
    }

    if (!isEdit) {
      submitData.password = formData.password;
    }

    try {
      if (isEdit) {
        await updateMutation.mutateAsync(submitData);
      } else {
        await createMutation.mutateAsync(submitData);
      }
    } catch (error) {
      // Error is handled by mutation
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <Shield className="h-6 w-6 text-primary-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {isEdit ? 'Edit Admin' : 'Add New Admin'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="form-label">Full Name *</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="input pl-10"
                  placeholder="Enter full name"
                  required
                />
              </div>
            </div>

            <div>
              <label className="form-label">Email *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="input pl-10"
                  placeholder="Enter email address"
                  required
                />
              </div>
            </div>

            <div>
              <label className="form-label">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="input"
                placeholder="Enter phone number"
              />
            </div>

            <div>
              <label className="form-label">Role *</label>
              <select
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                className="input"
                required
              >
                {getAvailableRoles().map(role => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
            </div>

            {!isEdit && (
              <>
                <div>
                  <label className="form-label">Password *</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className="input pr-10"
                      placeholder="Enter password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="form-label">Confirm Password *</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      className="input pr-10"
                      placeholder="Confirm password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="form-label">Designation</label>
              <input
                type="text"
                value={formData.designation}
                onChange={(e) => handleInputChange('designation', e.target.value)}
                className="input"
                placeholder="Enter designation"
              />
            </div>

            {shouldShowRegionalOffice() && (
              <div>
                <label className="form-label">Regional Office</label>
                <input
                  type="text"
                  value={formData.regionalOffice}
                  onChange={(e) => handleInputChange('regionalOffice', e.target.value)}
                  className="input"
                  placeholder="Enter regional office"
                />
              </div>
            )}

            {user?.role === 'regional_admin' && formData.role === 'regional_employee' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  ℹ️ Regional office will be automatically assigned to your office.
                </p>
              </div>
            )}

            <div>
              <label className="form-label">Status</label>
              <select
                value={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.value === 'true')}
                className="input"
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>

          {/* Permissions */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Permissions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(getAvailablePermissions()).map(([module, availablePermissions]) => {
                const formPermissions = formData.permissions[module] || { view: false, write: false, delete: false };
                return (
                  <div key={module} className="border rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3 capitalize">
                      {module.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                    </h4>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formPermissions.view}
                          onChange={(e) => handlePermissionChange(module, 'view', e.target.checked)}
                          className="checkbox"
                        />
                        <span className="text-sm">View</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formPermissions.write}
                          onChange={(e) => handlePermissionChange(module, 'write', e.target.checked)}
                          className="checkbox"
                        />
                        <span className="text-sm">Write</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formPermissions.delete}
                          onChange={(e) => handlePermissionChange(module, 'delete', e.target.checked)}
                          className="checkbox"
                        />
                        <span className="text-sm">Delete</span>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-outline"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading || updateMutation.isLoading}
              className="btn btn-primary"
            >
              {createMutation.isLoading || updateMutation.isLoading ? (
                'Saving...'
              ) : (
                isEdit ? 'Update Admin' : 'Create Admin'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAdminModal; 